import type { useNavigate } from "@tanstack/react-router"
import { useCallback } from "react"

interface UseSearchNavigateFilterProps<LF> {
  filtersDefaultValue: Partial<LF>
  currentNavigate: ReturnType<typeof useNavigate>
}

interface UseSearchNavigateFilterReturn<LF> {
  clearFilterAll: () => void
  clearFilterField: (name: keyof LF, nestedValueToRemove?: string | number) => void
  enableFilterIfChecked: (name: string, checked: boolean | null | undefined) => void
  changeFilter: (name: string, value: any) => void
  changeFilters: (items: Partial<LF>) => void
}

export function useSearchNavigateFilter<LF>({
  filtersDefaultValue,
  currentNavigate,
}: UseSearchNavigateFilterProps<LF>): UseSearchNavigateFilterReturn<LF> {
  const clearFilterField = useCallback(
    (name: keyof LF, nestedValueToRemove?: string | number) => {
      if (!name) {
        console.warn("No filter name provided")
        return
      }
      // If a specific filter is provided, clear that filter
      currentNavigate({
        // @ts-expect-error in this generic context, navigate do not have tanstack react router types
        search: (prev: Record<string, any>): Record<string, any> => ({
          ...prev,
          [name]: nestedValueToRemove
            ? prev[name as string]?.filter((item: string | number) => item !== nestedValueToRemove)
            : undefined,
        }),
        replace: true,
      })
    },
    [currentNavigate]
  )

  const clearFilterAll = useCallback(() => {
    currentNavigate({
      // @ts-expect-error in this generic context, navigate do not have tanstack react router types
      search: (prev: Record<string, any>): Record<string, any> => ({
        ...prev,
        ...filtersDefaultValue,
      }),
      replace: true,
    })
  }, [currentNavigate, filtersDefaultValue])

  const changeFilter = useCallback(
    (name: string, value: any) => {
      if (!name) {
        console.warn("No filter name provided")
        return
      }
      currentNavigate({
        // @ts-expect-error in this generic context, navigate do not have tanstack react router types
        search: (prev: Record<string, any>): Record<string, any> => ({
          ...prev,
          [name]: value,
        }),
        replace: true,
      })
    },
    [currentNavigate]
  )

  const enableFilterIfChecked = useCallback(
    (name: string, checked: boolean | null | undefined) => {
      changeFilter(name, checked ? checked : undefined)
    },
    [changeFilter]
  )

  const changeFilters = useCallback(
    (items: Partial<LF>) => {
      if (!items) {
        console.warn("No items provided")
        return
      }
      currentNavigate({
        // @ts-expect-error in this generic context, navigate do not have tanstack react router types
        search: (prev: Record<string, any>): Record<string, any> => ({
          ...prev,
          ...items,
        }),
        replace: true,
      })
    },
    [currentNavigate]
  )

  return {
    clearFilterAll,
    clearFilterField,
    enableFilterIfChecked,
    changeFilter,
    changeFilters,
  }
}
